/**
 * CtaList - Core Component (Standalone)
 *
 * This is a standalone component for rendering a list of CTA (Call-to-Action)
 * buttons from CMS data. It does NOT wrap a shadcn/ui component.
 *
 * Features:
 * - Uses generic variant/icon values from CMS data (CMS-agnostic)
 * - SmartLink handles external/internal/download links automatically
 * - Flexible layout with flex-wrap
 *
 * Supported variants: default, secondary, outline, ghost, link
 * Supported icons: arrow-right, chevron-right
 *
 * Uses: Button component from Core, SmartLink for link handling
 */
import { ArrowRight, ChevronRight, Download } from 'lucide-react';
import React from 'react';
import { Button, ButtonVariant } from '@/app/_components/core/button';
import { DownloadButton } from '@/app/_components/core/download-button';
import { SmartLink } from '@/app/_components/core/smart-link';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { getDownloadUrl, getFilenameFromUrl } from '@/app/_lib/download-url';
import { cn } from '@/app/_lib/utils';
import type { Cta } from '@/entities/models/page';

// Derived from Zod schema - single source of truth
export type CtaItem = Cta;

export interface CtaListProps {
  ctas?: CtaItem[];
  className?: string;
  /** Class name(s) for buttons - can be a string or a function that receives the variant */
  buttonClassName?: string | ((variant: ButtonVariant) => string);
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  separator?: string;
  separatorClassName?: string;
  /**
   * Stable, code-assigned tracking namespace for the rendered CTAs (e.g. the
   * module name "hero"). Produces `data-track-id="<prefix>-cta-<item>"` where
   * `<item>` is the CTA's own uid when present, otherwise its 1-based index —
   * the item segment is always appended, even for a single CTA, so the id stays
   * format-stable when more CTAs are added. Omit to leave the CTAs untracked.
   * Never derive this from CMS text. See apps/showcase/TRACKING-INVENTORY.md.
   */
  trackingPrefix?: string;
}

// Map generic icon names to React components (type-safe)
type IconName = 'arrow-right' | 'chevron-right' | 'download';
const ICON_MAP: Record<IconName, React.ReactNode> = {
  'arrow-right': <ArrowRight />,
  'chevron-right': <ChevronRight />,
  'download': <Download />,
};

// Get icon component from generic icon name (type-safe lookup)
const getIconFromName = (iconName: string | undefined): React.ReactNode | null => {
  if (!iconName) return null;
  if (iconName in ICON_MAP) {
    return ICON_MAP[iconName as IconName];
  }
  return null;
};

export function CtaList({ ctas, className, buttonClassName, buttonSize = 'default', separator, separatorClassName, trackingPrefix }: CtaListProps) {
  const validCtas = ctas?.filter((cta) => cta.linkTitle && cta.linkUrl);
  if (!validCtas || validCtas.length === 0) {
    return null;
  }

  // Stable tracking id per CTA: "<prefix>-cta-<item>" where <item> is the CTA's
  // own uid when present, else its 1-based index. The item segment is always
  // appended (also for a single CTA) so the id stays format-stable when more
  // CTAs are added. Undefined when no prefix is set, leaving the CTA untracked.
  const trackId = (cta: CtaItem, index: number): string | undefined =>
    trackingPrefix
      ? `${trackingPrefix}-cta-${getItemTrackId(cta.id, index)}`
      : undefined;

  return (
    <div className={cn('flex flex-wrap items-center gap-4', className)}>
      {validCtas.map((cta, index) => {
        const showSeparator = separator && index === 0 && validCtas.length > 1;
        const variant = (cta.variant as ButtonVariant | undefined) ?? 'default';
        const icon = getIconFromName(cta.icon);
        const isLinkVariant = variant === 'link';
        const isGhostVariant = variant === 'ghost';
        const isDownload = cta.download === true;
        // ghost and link variants show only toast, no loading UI
        const useSimpleDownload = isDownload && (isLinkVariant || isGhostVariant);

        // Cap CTA button width at 288px on mobile so long CMS link titles
        // don't blow out a narrow viewport — the label truncates with an
        // ellipsis. From md (≥ 768px) the cap is lifted, full text shows.
        const baseButtonClassName = isLinkVariant
          ? '!p-0 !h-auto max-w-72 md:max-w-none'
          : 'max-w-72 md:max-w-none';
        const resolvedButtonClassName =
          typeof buttonClassName === 'function'
            ? buttonClassName(variant)
            : buttonClassName;
        const mergedButtonClassName = cn(baseButtonClassName, resolvedButtonClassName);

        // Render appropriate button type based on link type
        const renderButton = () => {
          // Download with loading UI (default, secondary, outline, destructive variants)
          // DownloadButton is a special button with loading state - stays separate
          if (isDownload && !useSimpleDownload) {
            const downloadHref = getDownloadUrl(cta.linkUrl, getFilenameFromUrl(cta.linkUrl));
            return (
              <DownloadButton
                key={index}
                href={downloadHref}
                filename={getFilenameFromUrl(cta.linkUrl)}
                variant={variant}
                size={isLinkVariant ? undefined : buttonSize}
                className={mergedButtonClassName}
                endIcon={icon}
                data-track-id={trackId(cta, index)}
              >
                <span className="truncate">{cta.linkTitle}</span>
              </DownloadButton>
            );
          }

          // All other cases: Button + SmartLink
          // SmartLink handles: external, internal, mailto, tel, and simple downloads
          return (
            <Button
              key={index}
              variant={variant}
              size={isLinkVariant ? undefined : buttonSize}
              className={mergedButtonClassName}
              asChild
            >
              <SmartLink
                href={cta.linkUrl}
                target={cta.linkTarget}
                download={useSimpleDownload ? true : undefined}
                data-track-id={trackId(cta, index)}
              >
                <span className="truncate">{cta.linkTitle}</span>
                {icon && (
                  <span className="inline-flex shrink-0 items-center">
                    {icon}
                  </span>
                )}
              </SmartLink>
            </Button>
          );
        };

        const button = renderButton();

        return (
          <React.Fragment key={index}>
            {button}
            {showSeparator && <span className={cn('text-inherit', separatorClassName)}>{separator}</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
}
